import React, { useCallback, useEffect, useState } from 'react';
import Immutable from 'immutable';
import type { AudioSourceData } from '@lumen5/luminary';

import type { AudioAssetModel } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { usePlaybackFunctionality } from 'js/src/components/app/audio-tab/usePlaybackFunctionality';
import Waveform from 'js/src/components/app/waveform/Waveform';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isError } from 'js/src/types';

export const WAVEFORM_NUMBER_OF_BARS = 100;

type AudioWaveformPresentationProps = {
    media: ImmutableMap<AudioAssetModel | AudioSourceData>;
};

const AudioWaveformPresentation = ({ media }: AudioWaveformPresentationProps) => {
    const [waveformData, setWaveformData] = useState(Immutable.List<number>([]));
    const [fillPercentage, setFillPercentage] = useState(0);

    const { seek, isCurrentPlayingTrack, currentTimePercentage } = usePlaybackFunctionality(media);
    const missingUUIDErrorMesssage = 'Asset does not have uuid';

    const fetchWaveform = useCallback(() => {
        return new Promise<Immutable.List<number>>((resolve, reject) => {
            const waveform = (media as ImmutableMap<AudioAssetModel>).get('waveform');
            if (waveform) {
                resolve(waveform);
                return;
            }
            const uuid = media.get('uuid');
            if (!uuid) {
                reject(new Error(missingUUIDErrorMesssage));
                return;
            }
            ApiService.audioassetRetrieve({ path: { uuid: uuid } })
                .then((response) => {
                    if (response.data.waveform) {
                        const waveformData = response.data.waveform;
                        resolve(Immutable.List<number>(waveformData));
                    }
                })
                .catch((err: unknown) => {
                    ErrorReporter.capture(err);
                });
        });
    }, [media]);

    useEffect(() => {
        (async () => {
            let waveform = null;
            try {
                waveform = await fetchWaveform();
            } catch (err: unknown) {
                if (isError(err) && err.message !== missingUUIDErrorMesssage) {
                    ErrorReporter.capture(err);
                }
            }
            if (waveform) {
                setWaveformData(waveform.slice(0, WAVEFORM_NUMBER_OF_BARS));
            }
        })();
    }, [fetchWaveform]);

    useEffect(() => {
        const value = isCurrentPlayingTrack ? currentTimePercentage : 0;
        setFillPercentage(value);
    }, [currentTimePercentage, isCurrentPlayingTrack]);

    return <Waveform waveformData={waveformData} fillPercentage={fillPercentage} onSeek={seek} />;
};

const AudioWaveformMemoized = React.memo(AudioWaveformPresentation);
const AudioWaveform = AudioWaveformMemoized;

export default AudioWaveform;
